import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input, notify } from '../../../components/ui';
import { useApiError } from '../../../hooks/useApiError';
import api, { getApiError } from '../../../services/api';
import type { Table } from '../../../types/Table';
import { MAX_LABEL, MAX_RANGE, parseTableInput } from '../tableInput';

interface Props {
    open: boolean;
    onClose: () => void;
    locationId: string;
    onAdded: () => void;
}

export const AddTablesDialog = ({ open, onClose, locationId, onAdded }: Props) => {
    const { t } = useTranslation('locations');
    const apiError = useApiError();
    const [raw, setRaw] = useState('');
    const [description, setDescription] = useState('');
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);

    const parsed = parseTableInput(raw);
    const error = touched && parsed.kind === 'error' ? t(`tables.errors.${parsed.reason}`, { max: MAX_RANGE, maxLabel: MAX_LABEL }) : undefined;
    const single = parsed.kind === 'none' || (parsed.kind === 'list' && parsed.labels.length === 1);

    const close = () => {
        setRaw('');
        setDescription('');
        setTouched(false);
        onClose();
    };

    const submit = async () => {
        setTouched(true);
        if (parsed.kind === 'error') return;
        setBusy(true);
        try {
            let created = 0;
            let skipped = 0;
            if (parsed.kind === 'range') {
                const { data } = await api.post<Table[]>(`/locations/${locationId}/tables/bulk`, { from: parsed.from, to: parsed.to });
                created = data.length;
                skipped = parsed.to - parsed.from + 1 - created;
            } else {
                const note = single && description.trim() ? { description: description.trim() } : {};
                const labels = parsed.kind === 'list' ? parsed.labels : [null];
                const results = await Promise.allSettled(
                    labels.map((tableNumber) => api.post(`/locations/${locationId}/tables`, tableNumber === null ? note : { tableNumber, ...note }))
                );
                const failures = results.flatMap((r) => (r.status === 'rejected' ? [r.reason as unknown] : []));
                const other = failures.find((f) => getApiError(f).status !== 409);
                created = results.length - failures.length;
                skipped = failures.length - (other ? 1 : 0);
                if (other) throw other;
            }
            if (created > 0) notify.success(t('tables.added', { count: created }) + (skipped > 0 ? ` ${t('tables.skipped', { count: skipped })}` : ''));
            else notify.error(t('tables.noneAdded'));
            onAdded();
            if (created > 0) close();
        } catch (e) {
            onAdded();
            notify.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            busy={busy}
            title={t('tables.addTitle')}
            description={t('tables.addBody')}
            footer={
                <>
                    <Button variant="quiet" onClick={close} disabled={busy}>
                        {t('common:ui.cancel')}
                    </Button>
                    <Button variant="primary" onClick={submit} loading={busy}>
                        {parsed.kind === 'none' ? t('tables.addOne') : t('tables.add')}
                    </Button>
                </>
            }
        >
            <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                }}
            >
                <Field label={t('tables.labelOrRange')} hint={t('tables.labelHint', { max: MAX_RANGE })} error={error} optional>
                    {(c) => <Input {...c} data-autofocus value={raw} onChange={(e) => setRaw(e.target.value)} onBlur={() => setTouched(true)} autoComplete="off" />}
                </Field>
                <Field label={t('tables.description')} hint={single ? undefined : t('tables.descriptionSingle')} optional>
                    {(c) => <Input {...c} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!single} maxLength={100} autoComplete="off" />}
                </Field>
                <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
            </form>
        </Dialog>
    );
};
